// packages/supabase-client/src/react/pages/LoginPage.tsx

import React, { useState } from 'react';
import { AuthLayout } from '../components/AuthLayout';
import { SignInForm } from '../forms';
import { MagicLinkForm } from '../forms/MagicLinkForm';
import { OAuthProviders } from '../components';
import { AuthFormData } from '../types/form';
import { Button } from '@dopomogai/ui';
import { AuthAlert } from '../components';
import { useAuthService, useAuthText, useAuthUI, useAuthErrorMessage, useAuthFlow } from '../hooks';

type AuthMethod = 'password' | 'magicLink';

export const LoginPage: React.FC = () => {
  const authService = useAuthService();
  const { text, resolveText } = useAuthText();
  const { branding, layout, redirects } = useAuthUI();
  const { resolveErrorMessage } = useAuthErrorMessage();

  const [isSuccess, setIsSuccess] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password');
  const [magicLinkEmail, setMagicLinkEmail] = useState<string>('');
  const flow = useAuthFlow();

  const handleSignIn = async (data: Pick<AuthFormData, 'email' | 'password'>) => {
    flow.start();

    const result = await authService.signInWithEmailPassword(data.email, data.password);

    if (!result.success) {
      flow.fail(resolveErrorMessage(result.error));
      return;
    }
    flow.reset();
  };

  // Success view after magic link sent
  if (isSuccess) {
    const brandingSubtitle = branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle');
    const resendText = resolveText(text.resendMagicLinkButton, 'auth:resendMagicLinkButton', { defaultValue: 'Resend link' });
    const resendTextIsString = typeof resendText === 'string' || typeof resendText === 'number';
    const usePasswordText = resolveText(text.usePassword, 'auth:usePassword', { defaultValue: 'Use password' });
    const usePasswordTextIsString = typeof usePasswordText === 'string' || typeof usePasswordText === 'number';

    const handleResend = async () => {
      if (!magicLinkEmail) return;
      flow.start();
      const result = await authService.signInWithOtp(magicLinkEmail, {
        emailRedirectTo: redirects.magicLinkRedirectTo,
      });
      if (!result.success) {
        flow.fail(resolveErrorMessage(result.error));
        return;
      }
      flow.reset();
    };

    return (
      <AuthLayout
        title={resolveText(text.magicLinkSentTitle, 'auth:magicLinkSentTitle')}
        subtitle={resolveText(text.magicLinkSentSubtitle, 'auth:magicLinkSentSubtitle')}
        brandingSubtitle={brandingSubtitle}
        brandingLogo={branding.logo}
        showBrandPanel={branding.showBrandPanel}
        variant={layout.variant}
        rootClassName={layout.rootClassName}
        brandPanelClassName={layout.brandPanelClassName}
        formPanelClassName={layout.formPanelClassName}
        cardClassName={layout.cardClassName}
        titleClassName={layout.titleClassName}
        subtitleClassName={layout.subtitleClassName}
      >
        <div className="text-center">
          <p className="text-slate-600">{resolveText(text.checkYourEmailForLogin, 'auth:checkYourEmailForLogin')}</p>
        </div>
        {flow.error && <AuthAlert message={flow.error} className="mt-4" />}
        <div className="mt-6 flex flex-col gap-3">
          <Button
            variant="primary"
            onClick={handleResend}
            disabled={flow.isPending || !magicLinkEmail}
            loading={flow.isPending}
            text={resendTextIsString ? String(resendText) : undefined}
          >
            {!resendTextIsString ? resendText : null}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setIsSuccess(false);
              setAuthMethod('password');
              flow.reset();
            }}
            text={usePasswordTextIsString ? String(usePasswordText) : undefined}
          >
            {!usePasswordTextIsString ? usePasswordText : null}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Simple inline divider
  const Divider = () => (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t border-slate-300" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-white px-2 text-slate-500">{resolveText(text.orDivider, 'common:or')}</span>
      </div>
    </div>
  );

  return (
    <AuthLayout
      title={resolveText(text.signInTitle, 'auth:signInTitle')}
      subtitle={authMethod === 'password' ? resolveText(text.signInSubtitle, 'auth:signInSubtitle') : resolveText(text.signInMagicLinkSubtitle, 'auth:signInMagicLinkSubtitle')}
      brandingSubtitle={branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle')}
      brandingLogo={branding.logo}
      showBrandPanel={branding.showBrandPanel}
      variant={layout.variant}
      rootClassName={layout.rootClassName}
      brandPanelClassName={layout.brandPanelClassName}
      formPanelClassName={layout.formPanelClassName}
      cardClassName={layout.cardClassName}
      titleClassName={layout.titleClassName}
      subtitleClassName={layout.subtitleClassName}
    >
      <OAuthProviders />
      <Divider />

      {authMethod === 'password' ? (
        <SignInForm onSubmit={handleSignIn} isPending={flow.isPending} errorMessage={flow.error} />
      ) : (
        <MagicLinkForm
          onSubmit={async ({ email }: { email: string }) => {
            flow.start();
            setMagicLinkEmail(email);
            const result = await authService.signInWithOtp(email, {
              emailRedirectTo: redirects.magicLinkRedirectTo,
            });
            if (result.success) {
              flow.reset();
              setIsSuccess(true);
            } else {
              flow.fail(resolveErrorMessage(result.error));
            }
          }}
          isPending={flow.isPending}
          errorMessage={flow.error}
          initialEmail={magicLinkEmail}
          onEmailChange={setMagicLinkEmail}
        />
      )}

      <div className="mt-6 text-center">
        <button
          onClick={() => {
            setAuthMethod(authMethod === 'password' ? 'magicLink' : 'password');
            flow.reset();
          }}
          className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors text-sm"
        >
          {authMethod === 'password' ? resolveText(text.useMagicLink, 'auth:useMagicLink') : resolveText(text.usePassword, 'auth:usePassword')}
        </button>
      </div>
    </AuthLayout>
  );
};
